import { createContext, useCallback, useContext, useState, ReactNode } from 'react';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Optional click handler (e.g. navigate to a page) */
  onClick?: () => void;
  durationMs?: number;
}

interface ToastContextValue {
  showToast: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID();
      const full: Toast = { id, durationMs: 5000, ...toast };
      setToasts((prev) => [...prev, full]);
      window.setTimeout(() => dismiss(id), full.durationMs);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-16 right-3 sm:right-4 z-[100] flex flex-col gap-2 w-[calc(100vw-1.5rem)] max-w-sm pointer-events-none">
        {toasts.map((toast) => (
          <button
            key={toast.id}
            type="button"
            onClick={() => {
              toast.onClick?.();
              dismiss(toast.id);
            }}
            className="pointer-events-auto text-left w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-3 flex items-start gap-3 animate-[slideIn_0.2s_ease-out] hover:shadow-xl transition-shadow"
          >
            {toast.icon && <div className="flex-shrink-0 mt-0.5">{toast.icon}</div>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {toast.title}
              </p>
              {toast.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                  {toast.description}
                </p>
              )}
            </div>
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                dismiss(toast.id);
              }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none px-1"
            >
              ×
            </span>
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
